import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, FileUp, CheckCircle2, Dot, AlarmClock } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ICON = { submission: FileUp, decision: CheckCircle2, flag: AlarmClock };

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    const onRefresh = () => load();
    window.addEventListener("notif:refresh", onRefresh);
    return () => { clearInterval(timer.current); window.removeEventListener("notif:refresh", onRefresh); };
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        const { data } = await api.post(`/notifications/${n.id}/read`);
        setUnread(data.unread ?? 0);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch { /* ignore */ }
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    } catch { /* ignore */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger data-testid="notif-bell" className="relative outline-none rounded-full p-2 hover:bg-secondary transition-colors">
        <Bell className="h-5 w-5 text-foreground" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 bg-white" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-heading font-semibold text-sm">Notifications</p>
          {unread > 0 && (
            <button data-testid="notif-mark-all" onClick={markAll} className="text-[11px] brand-text hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notif-empty">You're all caught up.</p>
          )}
          {items.map((n) => {
            const Icon = ICON[n.kind] || Bell;
            return (
              <button
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border/60 hover:bg-secondary/60 transition-colors ${n.read ? "" : "bg-[rgb(var(--brand)/0.05)]"}`}
              >
                <div className="mt-0.5 shrink-0"><Icon className="h-4 w-4 brand-text" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground flex items-center gap-1">
                    {!n.read && <Dot className="h-4 w-4 text-red-600 -ml-1.5 shrink-0" />}
                    {n.title}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            );
          })}
        </div>
        <button
          data-testid="notif-view-all"
          onClick={() => { setOpen(false); navigate("/notifications"); }}
          className="w-full border-t border-border px-4 py-2.5 text-center text-xs font-medium brand-text hover:bg-secondary/60 transition-colors"
        >
          View all notifications
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
